import { Component, Vue, Prop } from "vue-property-decorator";
import {
	Spin,
	Icon,
	Form,
	Row,
	Col,
	Input,
	InputNumber,
	Select,
	Checkbox,
	Dropdown,
	Menu,
	Upload
} from "ant-design-vue";
import { WrappedFormUtils, FieldDecoratorOptions } from "ant-design-vue/lib";
import "@grapecity/spread-sheets-vue";
import GC from "@grapecity/spread-sheets";
import { IO } from "@grapecity/spread-excelio";
import { fmtMsg, GLUtil, ActionItem } from "commonui";
import { LessonPlanLocale, CommonLocale } from "@/locales/localeid";
import { ILessonPlanTemplateModel, LessonPlanTemplateColumns } from "@/models/lesson-plan";
import { spaceValidator, getRangeArray } from "@/util/func";
import { LessonPlanTemplateRowsTypeStyleMap, MethodologyTypeItemsForTemplate } from "@/util/constants";
import RowsIndicatorRect from "./rows-indicator/rows-indicator.vue";
import { LessonPlanTemplateRowsType, MethodologyType } from "@/util/enum";

@Component({
	components: {
		"a-spin": Spin,
		Icon,
		"a-form": Form,
		"a-form-item": Form.Item,
		"a-row": Row,
		"a-col": Col,
		"a-input": Input,
		"a-select": Select,
		"a-select-option": Select.Option,
		"a-checkbox": Checkbox,
		"a-dropdown": Dropdown,
		"a-menu": Menu,
		"a-menu-item": Menu.Item,
		"a-upload": Upload,
		ActionItem,
		RowsIndicatorRect
	}
})
export default class TemplateEditorContent extends Vue {
	@Prop() lessonPlanTemplate!: ILessonPlanTemplateModel; // = { headerRows: 4, teachingPlanRows: "5-7", transitionRows: 8 };
	@Prop() loadingData!: boolean;
	@Prop() disableSave!: boolean;
	@Prop() onChangeDisableSave!: Function;

	private editorForm: WrappedFormUtils;
	private spread: GC.Spread.Sheets.Workbook | null = null;
	private inputElement: HTMLInputElement | null = null;
	private loadData: { loading: boolean } = { loading: false };

	created(): void {
		this.editorForm = this.$form.createForm(this, {
			name: "editorForm",
			onValuesChange: this.onValuesChange
		});
	}

	private onValuesChange() {
		this.onChangeDisableSave(false);
	}

	mounted() {}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	get lessonPlanLocale(): any {
		return LessonPlanLocale;
	}

	get commonLocale(): any {
		return CommonLocale;
	}

	get loading(): boolean {
		return this.loadingData || this.loadData.loading;
	}

	get lessonPlanTemplateRowsType(): any {
		return LessonPlanTemplateRowsType;
	}

	get methodologyItems(): Map<MethodologyType, string> {
		return MethodologyTypeItemsForTemplate;
	}

	get nameDecorator(): [string, FieldDecoratorOptions] {
		return [
			LessonPlanTemplateColumns.NameColumn,
			{
				initialValue: this.lessonPlanTemplate.name,
				rules: [
					{
						required: true,
						message: fmtMsg(LessonPlanLocale.LessonPlanTemplateEditNameRequireMessage),
						trigger: "change"
					},
					{
						max: 100,
						message: fmtMsg(LessonPlanLocale.LessonPlanVersionNameOverflowMessage),
						trigger: "change"
					},
					{
						validator: (rule, value, callback) => {
							return spaceValidator(
								this.editorForm,
								LessonPlanTemplateColumns.NameColumn,
								fmtMsg(LessonPlanLocale.LessonPlanTemplateEditNameRequireMessage),
								rule,
								value,
								callback
							);
						}
					}
				]
			}
		];
	}

	get methodologyDecorator(): [string, FieldDecoratorOptions] {
		return [
			LessonPlanTemplateColumns.MethodologyColumn,
			{
				initialValue: this.lessonPlanTemplate.methodology,
				rules: [
					{
						required: true,
						message: fmtMsg(LessonPlanLocale.LessonPlanTemplateEditMethodologyRequireMessage)
					}
				]
			}
		];
	}

	get defaultDecorator(): [string, FieldDecoratorOptions] {
		return [
			LessonPlanTemplateColumns.DefaultColumn,
			{
				initialValue: this.lessonPlanTemplate.default,
				valuePropName: "checked"
			}
		];
	}

	get templateFileDecorator(): [string, FieldDecoratorOptions] {
		if (this.lessonPlanTemplate.templateFile) {
			this.onLoadTemplate(this.lessonPlanTemplate.templateFile);
		}
		return [
			LessonPlanTemplateColumns.TemplateFile,
			{
				initialValue: this.lessonPlanTemplate.templateFile,
				valuePropName: LessonPlanTemplateColumns.TemplateFile,
				getValueFromEvent: this.onTemplateFileChanged.bind(this),
				rules: [
					{
						required: true,
						message: fmtMsg(LessonPlanLocale.LessonPlanTemplateEditTemplateFileRequireMessage)
					}
				]
			}
		];
	}

	get headerRowsDecorator(): [string, FieldDecoratorOptions] {
		return [
			LessonPlanTemplateColumns.HeaderRowRange,
			{
				initialValue: this.lessonPlanTemplate.headerRowRange,
				rules: [
					{
						validator: (rule, value, callback) => {
							return this.rowsNumberFormatValidator(
								this.editorForm,
								LessonPlanTemplateColumns.HeaderRowRange,
								undefined,
								rule,
								value,
								callback
							);
						},
						trigger: "blur"
					}
				]
			}
		];
	}

	get teachingPlanRowsDecorator(): [string, FieldDecoratorOptions] {
		return [
			LessonPlanTemplateColumns.TeachingPlanRowRange,
			{
				initialValue: this.lessonPlanTemplate.teachingPlanRowRange,
				rules: [
					{
						required: true,
						message: fmtMsg(LessonPlanLocale.LessonPlanTemplateEditTeachingPlanRowsRequireMessage)
					},
					{
						validator: (rule, value, callback) => {
							return this.rowsNumberFormatValidator(
								this.editorForm,
								LessonPlanTemplateColumns.TeachingPlanRowRange,
								fmtMsg(LessonPlanLocale.LessonPlanTemplateEditTeachingPlanRowsRequireMessage),
								rule,
								value,
								callback
							);
						},
						trigger: "blur"
					}
				]
			}
		];
	}

	get transitionRowsDecorator(): [string, FieldDecoratorOptions] {
		return [
			LessonPlanTemplateColumns.TransitionRowRange,
			{
				initialValue: this.lessonPlanTemplate.transitionRowRange,
				rules: [
					{
						required: true,
						message: fmtMsg(LessonPlanLocale.LessonPlanTemplateEditTransitionRowsRequireMessage)
					},
					{
						validator: (rule, value, callback) => {
							return this.rowsNumberFormatValidator(
								this.editorForm,
								LessonPlanTemplateColumns.TransitionRowRange,
								fmtMsg(LessonPlanLocale.LessonPlanTemplateEditTransitionRowsRequireMessage),
								rule,
								value,
								callback
							);
						},
						trigger: "blur"
					}
				]
			}
		];
	}

	initSpread(spread) {
		this.spread = spread;
		spread.suspendPaint();
		this.setSpreadOptions(spread);
		this.spread?.clearSheets();
		this.spread?.addSheet(0);
		const sheet = this.spread?.sheets[0];
		sheet?.name("Lesson Plan Template");
		this.setSpreadSheetOptions(sheet as GC.Spread.Sheets.Worksheet);
		//this.spread?.sheets.forEach(sheet => this.setSpreadSheetOptions(sheet));
		spread.resumePaint();
		this.loadData.loading = false;
	}

	setSpreadOptions(spread: GC.Spread.Sheets.Workbook) {
		spread.options.allowUserDeselect = false;
		spread.options.allowUserResize = false;
		spread.options.allowContextMenu = false;
		spread.options.newTabVisible = false;
	}

	setSpreadSheetOptions(sheet: GC.Spread.Sheets.Worksheet) {
		sheet.options.isProtected = true;
		sheet.options.gridline.showHorizontalGridline = false;
		sheet.options.gridline.showVerticalGridline = false;
		sheet.options.selectionBorderColor = "transparent";
		sheet.options.selectionBackColor = "transparent";
		sheet.selectionUnit(GC.Spread.Sheets.SelectionUnit.row);
		sheet.setColumnCount(2, GC.Spread.Sheets.SheetArea.rowHeader);
		// sheet.visible(false);
	}

	onSelectTemplateFile() {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";
		input.style.display = "none";
		document.body.appendChild(input);
		input.click();
		this.inputElement = input;
		input.addEventListener("change", this.onTemplateFileChanged.bind(this));
	}

	onTemplateFileChanged(event) {
		const templateFile = (event.target as HTMLInputElement)?.files?.[0];
		if (!templateFile) return;
		this.onLoadTemplate(templateFile);
		this.editorForm.setFieldsValue({ [LessonPlanTemplateColumns.TemplateFile]: templateFile });
		return templateFile;
	}

	onLoadTemplate(templateFile) {
		const spread = this.spread;
		const excelIo = new IO();
		const loadData = this.loadData;
		const setRowIndicators = this.setRowIndicators.bind(this);
		const setSpreadOptions = this.setSpreadOptions.bind(this);
		const setSpreadSheetOptions = this.setSpreadSheetOptions.bind(this);
		// here is excel IO API
		excelIo.open(
			templateFile,
			function (json) {
				const workbookObj = json;
				spread &&
					spread.fromJSON(workbookObj, {
						incrementalLoading: {
							loading: function (progress) {
								loadData.loading = true;
							},
							loaded: function () {
								loadData.loading = false;
								setSpreadOptions(spread);
								spread?.sheets.forEach((sheet) => setSpreadSheetOptions(sheet));
								spread?.getActiveSheet().setSelection(0, 0, 1, 1);
								setRowIndicators();
							}
						}
					});
			},
			function (e) {
				// process error
				loadData.loading = false;
				console.log(e.errorMessage);
			}
		);
	}

	onHeaderRowsIndicatorChanged(event) {
		this.onSetRowIndicator(event.target.value, LessonPlanTemplateRowsType.Header);
	}

	onTeachingPlanRowsIndicatorChanged(event) {
		this.onSetRowIndicator(event.target.value, LessonPlanTemplateRowsType.TeachingPlan);
	}

	onTransitionRowsIndicatorChanged(event) {
		this.onSetRowIndicator(event.target.value, LessonPlanTemplateRowsType.Transition);
	}

	getTemplateRows(lessonPlanTemplateRowsType: LessonPlanTemplateRowsType, rowRange?: string): number[] {
		let templateRowRange = rowRange;
		if (templateRowRange == undefined) {
			const { getFieldValue } = this.editorForm;
			switch (lessonPlanTemplateRowsType) {
				case LessonPlanTemplateRowsType.Header:
					templateRowRange = getFieldValue(LessonPlanTemplateColumns.HeaderRowRange);
					break;
				case LessonPlanTemplateRowsType.TeachingPlan:
					templateRowRange = getFieldValue(LessonPlanTemplateColumns.TeachingPlanRowRange);
					break;
				case LessonPlanTemplateRowsType.Transition:
				default:
					templateRowRange = getFieldValue(LessonPlanTemplateColumns.TransitionRowRange);
					break;
			}
		}
		return this.getRowsRange(templateRowRange as string);
	}

	getRowsRange(rowRange: string): number[] {
		const teachingPlanRowsRangeRegex = /(?<start>\d+)-(?<end>\d+)/g;
		const teachingPlanRowsNumberRegex = /(?<number>\d+)/g;
		//1.match number seperate with hyphen
		const templateRowRangeMatches = teachingPlanRowsRangeRegex.exec(rowRange);
		const rangeStart = templateRowRangeMatches?.groups?.start;
		const rangeEnd = templateRowRangeMatches?.groups?.end;
		const result: number[] = rangeStart && rangeEnd ? getRangeArray(rangeStart, rangeEnd) : [];
		//2.match number seperate with comma
		const templateRowNumbers = `${rowRange}`?.replace(teachingPlanRowsRangeRegex, "");
		let matches: RegExpExecArray | null = null;
		while ((matches = teachingPlanRowsNumberRegex.exec(templateRowNumbers)) !== null) {
			matches?.groups?.number !== undefined && result.push(parseInt(matches?.groups?.number, 10));
		}
		return result;
	}

	//sync row indicator on ui
	setRowIndicators() {
		const { getFieldValue } = this.editorForm;
		const headerRowRange = getFieldValue(LessonPlanTemplateColumns.HeaderRowRange);
		const teachingPlanRowRange = getFieldValue(LessonPlanTemplateColumns.TeachingPlanRowRange);
		const transitionRowRange = getFieldValue(LessonPlanTemplateColumns.TransitionRowRange);
		this.onSetRowIndicator(headerRowRange as string, LessonPlanTemplateRowsType.Header);
		this.onSetRowIndicator(teachingPlanRowRange as string, LessonPlanTemplateRowsType.TeachingPlan);
		this.onSetRowIndicator(transitionRowRange as string, LessonPlanTemplateRowsType.Transition);
	}

	onSetRowIndicator(rowRange: string, lessonPlanTemplateRowsType: LessonPlanTemplateRowsType) {
		const numberRangeRegx = /\d+(?:-\d+)?(?:,\d+(?:-\d+)?)*/;
		const sheet = this.spread?.getActiveSheet();
		const rowsCount = sheet ? sheet.getRowCount(GC.Spread.Sheets.SheetArea.viewport) : 0;
		const headerRowRange =
			lessonPlanTemplateRowsType == LessonPlanTemplateRowsType.Header
				? this.getTemplateRows(LessonPlanTemplateRowsType.Header, rowRange)
				: this.getTemplateRows(LessonPlanTemplateRowsType.Header);
		const teachingPlanRowRange =
			lessonPlanTemplateRowsType == LessonPlanTemplateRowsType.TeachingPlan
				? this.getTemplateRows(LessonPlanTemplateRowsType.TeachingPlan, rowRange)
				: this.getTemplateRows(LessonPlanTemplateRowsType.TeachingPlan);
		const transitionRowRange =
			lessonPlanTemplateRowsType == LessonPlanTemplateRowsType.Transition
				? this.getTemplateRows(LessonPlanTemplateRowsType.Transition, rowRange)
				: this.getTemplateRows(LessonPlanTemplateRowsType.Transition);
		const rowNumbersWithStyle: number[] = [
			...new Set([...headerRowRange, ...teachingPlanRowRange, ...transitionRowRange])
		];
		sheet?.suspendPaint();
		[...Array(rowsCount)].forEach((_, rowIndex) => {
			if (rowNumbersWithStyle.indexOf(rowIndex + 1) == -1) {
				sheet
					?.getCell(rowIndex, 0, GC.Spread.Sheets.SheetArea.rowHeader)
					.backColor(LessonPlanTemplateRowsTypeStyleMap.get(LessonPlanTemplateRowsType.None));
			}
		});
		headerRowRange.forEach(
			(rn) =>
				rn >= 0 &&
				sheet
					?.getCell(rn - 1, 0, GC.Spread.Sheets.SheetArea.rowHeader)
					.backColor(LessonPlanTemplateRowsTypeStyleMap.get(LessonPlanTemplateRowsType.Header))
		);
		teachingPlanRowRange.forEach(
			(rn) =>
				rn >= 0 &&
				sheet
					?.getCell(rn - 1, 0, GC.Spread.Sheets.SheetArea.rowHeader)
					.backColor(LessonPlanTemplateRowsTypeStyleMap.get(LessonPlanTemplateRowsType.TeachingPlan))
		);
		transitionRowRange.forEach(
			(rn) =>
				rn >= 0 &&
				sheet
					?.getCell(rn - 1, 0, GC.Spread.Sheets.SheetArea.rowHeader)
					.backColor(LessonPlanTemplateRowsTypeStyleMap.get(LessonPlanTemplateRowsType.Transition))
		);
		sheet?.resumePaint();
	}

	isRangeValid(rangeA: number[], rangeB: number[]): boolean {
		return rangeA.filter((x) => rangeB.includes(x)).length == 0;
	}

	rowsNumberFormatValidator(
		form: WrappedFormUtils,
		fieldName: string,
		emptyErrorMessage: string | undefined,
		rule: any,
		value: string,
		callback: (message?: string) => {}
	): void {
		if (fieldName == LessonPlanTemplateColumns.HeaderRowRange && (!value || value.trim() === "")) {
			callback();
		}
		if (emptyErrorMessage !== undefined && value && value.trim() === "") {
			callback(emptyErrorMessage);
		}
		const numberRegex = /^\d+$/;
		const numberRange = /^\d+-\d+$/;
		if (
			(numberRegex.exec(value.trim()) == null && numberRange.exec(value.trim()) == null) ||
			(value.trim() && this.getRowsRange(value.trim()).length == 0)
		) {
			const formatErrorMessage = fmtMsg(LessonPlanLocale.LessonPlanTemplateEditTemplateRowsFormatMessage);
			callback(formatErrorMessage);
		}
		//check row range is overflow
		const maxValue = this.spread?.getActiveSheet().getRowCount(GC.Spread.Sheets.SheetArea.viewport);
		const rowRange = this.getRowsRange(value.trim());
		if (rowRange.length > 0 && maxValue && rowRange.some((rowIndex) => rowIndex > maxValue)) {
			const overflowErrorMessage = fmtMsg(LessonPlanLocale.LessonPlanTemplateEditTemplateRowsOverflowMessage);
			callback(overflowErrorMessage);
		}
		callback();
	}

	isTemplateRowsNumberValid(): boolean {
		const { getFieldValue, setFields } = this.editorForm;
		//check whether duplicate row number exists.
		const headerRowRange = this.getTemplateRows(LessonPlanTemplateRowsType.Header);
		const teachingPlanRowRange = this.getTemplateRows(LessonPlanTemplateRowsType.TeachingPlan);
		const transitionRowRange = this.getTemplateRows(LessonPlanTemplateRowsType.Transition);
		const conflitErrorMessage = fmtMsg(LessonPlanLocale.LessonPlanTemplateEditTemplateRowsConflitMessage);
		const getCustomizedError = (fieldName, errorMessage?) => {
			return [
				{
					field: fieldName,
					message: errorMessage ? errorMessage : conflitErrorMessage
				}
			];
		};
		// console.log(headerRowRange, teachingPlanRowRange, transitionRowRange);
		//1.check HeaderRows and TeachingPlanRows
		if (!this.isRangeValid(headerRowRange, teachingPlanRowRange)) {
			setFields({
				[LessonPlanTemplateColumns.HeaderRowRange]: {
					value: getFieldValue(LessonPlanTemplateColumns.HeaderRowRange),
					errors: getCustomizedError(LessonPlanTemplateColumns.HeaderRowRange)
				},
				[LessonPlanTemplateColumns.TeachingPlanRowRange]: {
					value: getFieldValue(LessonPlanTemplateColumns.TeachingPlanRowRange),
					errors: getCustomizedError(LessonPlanTemplateColumns.TeachingPlanRowRange)
				}
			});
			return false;
		}
		//2.check HeaderRows and TransitionRows
		if (!this.isRangeValid(headerRowRange, transitionRowRange)) {
			setFields({
				[LessonPlanTemplateColumns.HeaderRowRange]: {
					value: getFieldValue(LessonPlanTemplateColumns.HeaderRowRange),
					errors: getCustomizedError(LessonPlanTemplateColumns.HeaderRowRange)
				},
				[LessonPlanTemplateColumns.TransitionRowRange]: {
					value: getFieldValue(LessonPlanTemplateColumns.TransitionRowRange),
					errors: getCustomizedError(LessonPlanTemplateColumns.TransitionRowRange)
				}
			});
			return false;
		}
		//3.check TeachingPlanRows and TransitionRows
		if (!this.isRangeValid(teachingPlanRowRange, transitionRowRange)) {
			setFields({
				[LessonPlanTemplateColumns.TeachingPlanRowRange]: {
					value: getFieldValue(LessonPlanTemplateColumns.TeachingPlanRowRange),
					errors: getCustomizedError(LessonPlanTemplateColumns.TeachingPlanRowRange)
				},
				[LessonPlanTemplateColumns.TransitionRowRange]: {
					value: getFieldValue(LessonPlanTemplateColumns.TransitionRowRange),
					errors: getCustomizedError(LessonPlanTemplateColumns.TransitionRowRange)
				}
			});
			return false;
		}
		return true;
	}

	onResetFormFieldsValue(): void {
		this.editorForm.resetFields();
		this.initSpread(this.spread);
	}

	onSubmitEditorForm(success: (values: any) => void, error: (errors: any, values: any) => void): void {
		const workbook = this.spread?.sheets[0];
		this.editorForm.validateFields((errors, values) => {
			if (errors && workbook) {
				error(errors, values);
			} else {
				const { getFieldValue, getFieldsError, setFields } = this.editorForm;
				const isTemplateRowsNumberValid = this.isTemplateRowsNumberValid();
				if (isTemplateRowsNumberValid) {
					const { templateFile } = values;
					//clear customized error
					setFields({
						[LessonPlanTemplateColumns.HeaderRowRange]: {
							value: getFieldValue(LessonPlanTemplateColumns.HeaderRowRange)
						},
						[LessonPlanTemplateColumns.TeachingPlanRowRange]: {
							value: getFieldValue(LessonPlanTemplateColumns.TeachingPlanRowRange)
						},
						[LessonPlanTemplateColumns.TransitionRowRange]: {
							value: getFieldValue(LessonPlanTemplateColumns.TransitionRowRange)
						}
					});
					success({ ...values, [LessonPlanTemplateColumns.TemplateFileName]: templateFile.name });
				} else {
					error(getFieldsError(), values);
				}
			}
		});
	}
}
